import { computed, type ComputedRef, inject, nextTick, ref, type Ref, watchEffect } from 'vue'
import { cloneDeep, isEqual } from 'lodash-es'
import { syncRef, useLocalStorage } from '@vueuse/core'

import {
  type ComponentNode,
  ComponentTypes,
  type SectionListProps,
  CanvasBuilderMode,
} from '@canvas-builder/types/canvas'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasVisibility from '@canvas-builder/composables/useCanvasVisibility'
import { getPageRange, scrollCanvasToTop, scrollToComponentById } from '@canvas-builder/util/canvas.util'
import type { CanvasRetrieve, CanvasSection } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types.ts'
import { PeerConnectivityActions, sendPeerConnectivityEvent } from '@sdk/peer-connectivity'
import { usePitcherApi, PitcherBroadcastedEventName } from '@sdk/main'

type AnimationDirection = 'Left' | 'Right'

type PreviousLocation = {
  componentId?: string
  sectionId?: string
  sectionIndex?: number
}

const ANIMATION_DIRECTION_DURATION = 1500
let animationDirectionTimeout: ReturnType<typeof setTimeout> | null = null
let isSyncingActiveSectionMetadata = false
let broadcastNavigationEvent: (direction: 'next' | 'previous') => Promise<void>
// data
const currentPageIndex = ref(0)
const sectionListIndexMap = ref<Record<ComponentNode['id'], number>>({})
const animationDirection = ref<AnimationDirection | ''>('')
const previousLocation = ref<PreviousLocation | null>(null)
const activeSectionMetadataLs = useLocalStorage<CanvasSection['metadata'] | null>('active-section-metadata', null, {
  deep: true,
  serializer: {
    read: (v: string) => {
      try {
        const parsed = JSON.parse(v)

        return parsed ?? null
      } catch {
        return null
      }
    },
    write: (v: any) => JSON.stringify(v),
  },
})

const setPreviousLocation = (location: PreviousLocation | null) => {
  previousLocation.value = location
}

// getters
const canvasContent: ComputedRef<ComponentNode[]> = computed(() => useCanvas().canvasContent.value)
const mode = computed(() => useCanvas().mode.value)
const pitcherSettings = computed(() => useCanvas().pitcherSettings.value)
const canvasPages: ComputedRef<ComponentNode[][]> = computed(() => {
  const { startPage, endPage, currentEmbedLocation } = useCanvas()
  const { getVisibleCanvasContent, getVisibleKey } = useCanvasVisibility()
  const visibleKey = getVisibleKey()
  const content = getVisibleCanvasContent(cloneDeep(canvasContent.value), visibleKey)

  return getPageRange(content, currentEmbedLocation.value, startPage.value, endPage.value)
})

const activeSectionId = computed(() => {
  const { componentNodesById } = useCanvas()
  if (!currentSectionList.value.sectionListId) return null

  const sectionId = (componentNodesById.value[currentSectionList.value.sectionListId].data as SectionListProps)
    .sections?.[currentSectionList.value.sectionListIndex]?.id
  if (!sectionId) return null
  return sectionId
})

const activeSection = computed(() => {
  const { activeCanvasSections } = useCanvas()
  if (!activeSectionId.value) return null

  return activeCanvasSections.value?.[activeSectionId.value]
})

const activeSectionTheme = computed(() => {
  const sectionTheme = activeSection.value?.theme
  if (!sectionTheme) return null

  return sectionTheme
})

const activeSectionMetadata = computed(() => {
  return activeSection.value?.metadata ?? null
})

const currentSectionList = computed(() => {
  const currentPageComponents = canvasPages.value?.[currentPageIndex.value]
  const sectionListComponent = currentPageComponents?.find((component): component is ComponentNode<SectionListProps> =>
    [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
  )
  if (sectionListComponent) {
    const sectionIndex = sectionListIndexMap.value[sectionListComponent.id] || 0
    return {
      sectionListId: sectionListComponent.id,
      sectionListIndex: sectionIndex,
    }
  }

  return {
    sectionListId: null,
    sectionListIndex: 0,
  }
})

const presentationProgress = computed(() => {
  let totalProgressUnits = 0 // Total number of pages and sections within section lists
  let completedProgressUnits = 0 // Completed pages and sections within section lists up to the current page

  // Calculate the total progress units and the number of completed progress units
  canvasPages.value.forEach((page, pageIndex) => {
    let isSectionListPage = false

    page.forEach((component) => {
      if ([ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type) && component.data) {
        isSectionListPage = true
        const sectionsLength = (component as ComponentNode<SectionListProps>)?.data?.sections.length || 0
        totalProgressUnits += sectionsLength // Each section in a section list counts as a unit

        if (pageIndex < currentPageIndex.value) {
          // All sections in section list pages before the current page are considered completed
          completedProgressUnits += sectionsLength
        } else if (pageIndex === currentPageIndex.value) {
          // Add the completed sections from the current section list page
          completedProgressUnits += (sectionListIndexMap.value[component.id] || 0) + 1
        }
      } else if (component.type === ComponentTypes.PageBreak) {
        totalProgressUnits++ // Each PageBreak counts as a unit
        if (pageIndex <= currentPageIndex.value) {
          completedProgressUnits++ // PageBreaks up to and including the current page are considered completed
        }
      }
    })

    if (!isSectionListPage) {
      totalProgressUnits++ // Non-section list pages count as one unit
      if (pageIndex <= currentPageIndex.value) {
        // Non-section list pages up to and including the current page are considered completed
        completedProgressUnits++
      }
    }
  })

  // Return the fraction of completed progress units to total progress units
  return [completedProgressUnits, totalProgressUnits]
})

const isFirstPage = computed(() => {
  if (canvasPages.value.length === 0) {
    return true
  }

  const sectionListId = currentSectionList.value.sectionListId
  // If there's no section list, or if we're at the first section of the section list, and it's the first page
  return currentPageIndex.value === 0 && (!sectionListId || !sectionListIndexMap.value[sectionListId])
})

const isLastPage = computed(() => {
  if (canvasPages.value.length === 0) {
    return true
  }

  const lastPageIndex = canvasPages.value.length ? canvasPages.value.length - 1 : 0
  const lastPageComponents = canvasPages.value?.[lastPageIndex]
  const lastSectionListComponent = lastPageComponents?.find?.(
    (component): component is ComponentNode<SectionListProps> =>
      [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
  )

  if (lastSectionListComponent?.data) {
    const lastSectionListIndex = lastSectionListComponent.data.sections.length - 1
    const currentSectionListId = currentSectionList.value.sectionListId
    if (!currentSectionListId) return false
    const currentSectionListIndex = sectionListIndexMap.value[currentSectionListId] || 0

    return (
      currentPageIndex.value === lastPageIndex &&
      currentSectionListId === lastSectionListComponent.id &&
      currentSectionListIndex === lastSectionListIndex
    )
  }

  const lastPageBreakComponent = lastPageComponents?.find?.(
    (component): component is ComponentNode => component.type === ComponentTypes.PageBreak,
  )

  if (lastPageBreakComponent) {
    return currentPageIndex.value === lastPageIndex
  }

  return currentPageIndex.value === lastPageIndex
})

function setAnimationDirection(direction: AnimationDirection) {
  animationDirectionTimeout && clearTimeout(animationDirectionTimeout)
  animationDirection.value = direction
  animationDirectionTimeout = setTimeout(() => {
    animationDirection.value = ''
  }, ANIMATION_DIRECTION_DURATION)
}

// actions
function goToNextPage() {
  if (isLastPage.value) return
  const currentPageComponents = canvasPages.value?.[currentPageIndex.value]
  const sectionListComponent = currentPageComponents?.find?.(
    (component): component is ComponentNode<SectionListProps> =>
      [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
  )

  if (sectionListComponent && sectionListComponent.data) {
    const currentSectionIndex = sectionListIndexMap.value[sectionListComponent.id] || 0
    if (currentSectionIndex < sectionListComponent.data.sections.length - 1) {
      // Increase section index if there are more sections
      sectionListIndexMap.value[sectionListComponent.id] = currentSectionIndex + 1
    } else {
      // Move to the next page and reset section index
      currentPageIndex.value++
      resetSectionListIndexForCurrentPage()
    }
  } else {
    // Move to the next page and reset section index
    currentPageIndex.value++
    resetSectionListIndexForCurrentPage()
  }

  setPreviousLocation(null)
  broadcastNavigationEvent('next')

  nextTick(() => {
    scrollCanvasToTop()
  })

  setAnimationDirection('Right')
}

function goToPreviousPage() {
  if (isFirstPage.value) return
  const currentPageComponents = canvasPages.value?.[currentPageIndex.value]
  const sectionListComponent = currentPageComponents?.find?.(
    (component): component is ComponentNode<SectionListProps> =>
      [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
  )

  if (sectionListComponent && sectionListComponent.data) {
    const currentSectionIndex = sectionListIndexMap.value[sectionListComponent.id] || 0
    if (currentSectionIndex > 0) {
      // Decrease section index if not at the first section
      sectionListIndexMap.value[sectionListComponent.id]--
    } else {
      // Move to the previous page and reset section index to last
      currentPageIndex.value--
      resetSectionListIndexForCurrentPage(true)
    }
  } else {
    // Move to the previous page and reset section index to last
    currentPageIndex.value--
    resetSectionListIndexForCurrentPage(true)
  }

  setPreviousLocation(null)
  broadcastNavigationEvent('previous')

  nextTick(() => {
    scrollCanvasToTop()
  })

  setAnimationDirection('Left')
}

// New function to reset section list index
function resetSectionListIndexForCurrentPage(isLast = false) {
  const currentPageComponents = canvasPages.value?.[currentPageIndex.value]
  const sectionListComponent = currentPageComponents?.find?.(
    (component): component is ComponentNode<SectionListProps> =>
      [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
  )

  if (sectionListComponent) {
    // If the current page is a section list, then reset the index to the first or last section
    const resetIndex =
      isLast && sectionListComponent.data?.sections?.length ? sectionListComponent.data.sections.length - 1 : 0
    sectionListIndexMap.value[sectionListComponent.id] = resetIndex
  } else {
    // If the current page is not a section list, clear the section list index map
    sectionListIndexMap.value = {}
  }
}

function goToFirstPage(componentId?: ComponentNode['id'], skipSetPreviousLocation?: boolean) {
  const initialPageLocation = currentPageLocation.value

  currentPageIndex.value = 0
  resetSectionListIndexForCurrentPage()

  if (!skipSetPreviousLocation && !isEqual(initialPageLocation, currentPageLocation.value)) {
    setPreviousLocation(initialPageLocation)
  }

  nextTick(() => {
    if (componentId) {
      // Check if the component exists on the first page
      const firstPage = canvasPages.value[0]
      const componentExists = firstPage?.some((component) => component.id === componentId)

      if (componentExists) {
        scrollToComponentById(componentId)
      } else {
        scrollCanvasToTop()
      }
    } else {
      scrollCanvasToTop()
    }
  })

  setAnimationDirection('Left')
}

function reset() {
  currentPageIndex.value = 0
  sectionListIndexMap.value = {}
  activeSectionMetadataLs.value = null
  isSyncingActiveSectionMetadata = false
  previousLocation.value = null
}

const getActiveSection = () => {
  const activePage = canvasPages.value[currentPageIndex.value]?.[0]
  const isSectionListPage = [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(activePage?.type)

  if (!isSectionListPage) {
    return undefined
  }

  const { id } = activePage
  const sectionQuantity = (activePage as ComponentNode<SectionListProps>)?.data?.sections.length ?? 0

  return {
    sectionQuantity,
    id,
  }
}

const changeActivePageWhenOutOfMinRange = () => {
  if (currentPageIndex.value < 0 && canvasPages.value.length > 0) {
    currentPageIndex.value = 0
  }
}

const changeActivePageWhenOutOfMaxRange = () => {
  if (currentPageIndex.value >= canvasPages.value.length) {
    currentPageIndex.value = canvasPages.value.length - 1

    const activeSection = getActiveSection()

    if (!activeSection) {
      return
    }

    sectionListIndexMap.value[activeSection.id] = activeSection.sectionQuantity - 1
  }
}

const changeActiveSectionListPageWhenOutOfMaxRange = () => {
  const activeSection = getActiveSection()

  if (!activeSection) {
    return
  }

  if (sectionListIndexMap.value[activeSection.id] >= activeSection.sectionQuantity) {
    sectionListIndexMap.value[activeSection.id] = activeSection.sectionQuantity - 1
  }
}

const keepActivePageValid = () => {
  watchEffect(() => changeActivePageWhenOutOfMinRange())
  watchEffect(() => changeActivePageWhenOutOfMaxRange())
  watchEffect(() => changeActiveSectionListPageWhenOutOfMaxRange())
}

const currentPageLocation = computed(() => {
  const currentPageComponents = canvasPages.value?.[currentPageIndex.value]

  const sectionListComponent = currentPageComponents?.find?.(
    (component): component is ComponentNode<SectionListProps> =>
      [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
  )

  const firstComponent = canvasPages.value?.[currentPageIndex.value]?.[0]

  let currentSectionIndex: undefined | number = undefined

  if (sectionListComponent && sectionListComponent.data) {
    currentSectionIndex = sectionListIndexMap.value[sectionListComponent.id] || 0
  }

  return {
    componentId: firstComponent?.id,
    sectionId: activeSectionId.value ?? undefined,
    sectionIndex: currentSectionIndex,
  }
})

export default function useCanvasPages() {
  if (!isSyncingActiveSectionMetadata) {
    syncRef(activeSectionMetadata, activeSectionMetadataLs, {
      immediate: true,
      direction: 'ltr',
      transform: {
        ltr: (v) => v,
      },
    })
    isSyncingActiveSectionMetadata = true
  }
  const { isEditMode } = useCanvas()

  const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
    'launchDarkly',
    computed(() => ({})),
  )

  const canvasOpenedFrom = inject<Ref<string | undefined>>('canvasOpenedFrom', ref(undefined))

  async function shouldBroadcastEvent(): Promise<boolean> {
    try {
      // Don't broadcast if canvas was opened via peer connectivity (to prevent echo)
      if (canvasOpenedFrom?.value === 'peer-connectivity') {
        return false
      }
      if (
        mode.value &&
        [CanvasBuilderMode.SHARING, CanvasBuilderMode.HOME, CanvasBuilderMode.ADMIN].includes(mode.value)
      ) {
        return false
      }
      const enableMultipeerConnectivity = pitcherSettings.value.enable_multipeer_connectivity ?? false
      if (!enableMultipeerConnectivity) {
        return false
      }
      const pitcherApi = usePitcherApi()
      const env = await pitcherApi.getEnv()
      if (env.mode !== 'IOS') {
        return false
      }
      return true
    } catch (error) {
      console.error('Failed to check broadcast conditions:', error)
      return false
    }
  }

  broadcastNavigationEvent = async (direction: 'next' | 'previous') => {
    if (!(await shouldBroadcastEvent())) {
      return
    }

    try {
      const { activeCanvas } = useCanvas()
      const pitcherApi = usePitcherApi()

      await sendPeerConnectivityEvent({
        action:
          direction === 'next'
            ? PeerConnectivityActions.CANVAS_PAGE_NEXT
            : PeerConnectivityActions.CANVAS_PAGE_PREVIOUS,
        context: {
          canvasId: activeCanvas.value?.id,
        },
        data: {
          currentPageIndex: currentPageIndex.value,
        },
      })
      await pitcherApi.broadcast({
        type:
          direction === 'next'
            ? PitcherBroadcastedEventName.CANVAS_NEXT_BUTTON_CLICKED
            : PitcherBroadcastedEventName.CANVAS_PREVIOUS_BUTTON_CLICKED,
        body: {
          canvas_id: activeCanvas.value?.id,
          current_page_index: currentPageIndex.value,
        },
      })
    } catch (error) {
      console.error('Failed to broadcast navigation event:', error)
    }
  }

  async function broadcastCanvasOpenedEvent() {
    if (!(await shouldBroadcastEvent())) {
      return
    }

    try {
      const { activeCanvas } = useCanvas()

      if (!activeCanvas.value?.id) {
        console.warn('Cannot broadcast canvas opened event: no active canvas')
        return
      }
      const pitcherApi = usePitcherApi()

      await sendPeerConnectivityEvent({
        action: PeerConnectivityActions.CANVAS_OPEN,
        context: {
          canvasId: activeCanvas.value.id,
        },
      })
      await pitcherApi.broadcast({
        type: PitcherBroadcastedEventName.CANVAS_OPENED,
        body: {
          canvas_id: activeCanvas.value.id,
        },
      })
    } catch (error) {
      console.error('Failed to broadcast canvas opened event:', error)
    }
  }

  async function broadcastCanvasClosedEvent(canvasId?: string) {
    if (!(await shouldBroadcastEvent())) {
      return
    }

    try {
      let targetCanvasId = canvasId
      if (!targetCanvasId) {
        const { activeCanvas } = useCanvas()
        targetCanvasId = activeCanvas.value?.id
      }

      if (!targetCanvasId) {
        console.warn('Cannot broadcast canvas closed event: no canvas ID available')
        return
      }
      const pitcherApi = usePitcherApi()

      await sendPeerConnectivityEvent({
        action: PeerConnectivityActions.CANVAS_CLOSE,
        context: {
          canvasId: targetCanvasId,
        },
      })
      await pitcherApi.broadcast({
        type: PitcherBroadcastedEventName.CANVAS_CLOSED,
        body: {
          canvas_id: targetCanvasId,
        },
      })
    } catch (error) {
      console.error('Failed to broadcast canvas closed event:', error)
    }
  }

  const isTocEnhancementsEnabled = computed(
    () => !!launchDarkly?.value?.enable_toc_enhancements_to_support_same_section_in_section_list,
  )

  const isBackToPreviousLocationVisible = computed(
    () => launchDarkly.value?.enable_back_to_previous_location_in_canvas && !isEditMode.value,
  )

  const isBackToPreviousLocationDisabled = computed(() => previousLocation.value === null)

  const backToPreviousLocation = () => {
    if (previousLocation.value === null || !previousLocation.value?.componentId) return

    goToPage({
      componentId: previousLocation.value.componentId,
      sectionId: previousLocation.value.sectionId,
      sectionIndex: previousLocation.value.sectionIndex,
      scrollTo: 'top',
      skipSetPreviousLocation: true,
    })

    setPreviousLocation(null)
  }

  function goToPage({
    componentId,
    sectionId,
    scrollTo,
    itemIndex,
    sectionIndex,
    skipSetPreviousLocation = false,
  }: {
    componentId: ComponentNode['id']
    sectionId?: CanvasRetrieve['id']
    scrollTo?: 'component' | 'top'
    itemIndex?: number
    sectionIndex?: number
    skipSetPreviousLocation?: boolean
  }) {
    const initialPageLocation = currentPageLocation.value

    // Find all pages containing components with the given ID
    const matchingPages: { pageIndex: number; component: ComponentNode }[] = []

    canvasPages.value.forEach((page, pageIdx) => {
      page.forEach((component) => {
        if (component.id === componentId) {
          matchingPages.push({ pageIndex: pageIdx, component })
        }
      })
    })

    let pageIndex = -1

    // If there are multiple matches and itemIndex is provided, use it to select the right one
    if (matchingPages.length > 1 && itemIndex !== undefined) {
      // Find the component at the specified index position
      if (itemIndex < matchingPages.length) {
        pageIndex = matchingPages[itemIndex].pageIndex
      } else {
        // Fallback to first match if index is out of range
        pageIndex = matchingPages[0].pageIndex
      }
    } else if (matchingPages.length > 0) {
      // Just use the first match if we don't have an index or only have one match
      pageIndex = matchingPages[0].pageIndex
    }

    // If the component is found within the pages
    if (pageIndex !== -1) {
      // Set the current page index to the page that contains the component
      currentPageIndex.value = pageIndex

      // If a sectionId is provided, handle the case where the component is a SectionList
      if (sectionId) {
        const sectionListComponent = canvasPages.value[pageIndex].find(
          (component): component is ComponentNode<SectionListProps> =>
            component.id === componentId &&
            [ComponentTypes.SectionList, ComponentTypes.ContentList].includes(component.type),
        )

        // If the section list component has data
        if (sectionListComponent && sectionListComponent.data) {
          // For sections with duplicate IDs, we need to handle selecting the correct occurrence
          if (itemIndex !== undefined) {
            // Count matching sections to find the occurrence specified by itemIndex
            let occurrenceCount = 0
            let targetSectionIndex = -1

            // Go through all sections to find the specified occurrence
            for (let i = 0; i < sectionListComponent.data.sections.length; i++) {
              const section = sectionListComponent.data.sections[i]

              if (section.id === sectionId) {
                if (occurrenceCount === itemIndex) {
                  // This is the occurrence we want
                  targetSectionIndex = i
                  break
                }
                occurrenceCount++
              }
            }

            if (targetSectionIndex !== -1) {
              // Set the section index to the correct occurrence
              sectionListIndexMap.value[componentId] = targetSectionIndex
            } else {
              // Fallback to first occurrence if we can't find the specified one
              const firstIndex = sectionListComponent.data.sections.findIndex((section) => section.id === sectionId)

              if (firstIndex !== -1) {
                sectionListIndexMap.value[componentId] = firstIndex
              } else {
                sectionListIndexMap.value[componentId] = 0
              }
            }
          } else {
            // If no itemIndex provided, use traditional first-match approach
            const sectionIndex = sectionListComponent.data.sections.findIndex((section) => section.id === sectionId)

            // If the section is found within the section list
            if (sectionIndex !== -1) {
              // Set the section index for the section list
              sectionListIndexMap.value[componentId] = sectionIndex
            } else {
              // If the sectionId is not found, reset to the first section
              sectionListIndexMap.value[componentId] = 0
            }
          }
        }
      }

      if (!skipSetPreviousLocation && !isEqual(initialPageLocation, currentPageLocation.value)) {
        setPreviousLocation(initialPageLocation)
      }

      nextTick(() => {
        if (scrollTo === 'top') scrollCanvasToTop()
        else if (componentId && scrollTo === 'component')
          scrollToComponentById(
            componentId,
            isTocEnhancementsEnabled.value ? sectionId : undefined,
            isTocEnhancementsEnabled.value ? sectionIndex : undefined,
          )
      })
    }
  }

  return {
    activeSection,
    activeSectionId,
    activeSectionTheme,
    canvasPages,
    currentPageIndex,
    currentSectionList,
    sectionListIndexMap,
    presentationProgress,
    isFirstPage,
    isLastPage,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    goToFirstPage,
    reset,
    keepActivePageValid,
    animationDirection,
    broadcastCanvasOpenedEvent,
    broadcastCanvasClosedEvent,
    backToPreviousLocation,
    isBackToPreviousLocationVisible,
    isBackToPreviousLocationDisabled,
  }
}
